// src/utils/whatsapp.js
// ─────────────────────────────────────────────────────────────────
// Envío por wa.me link — abre WhatsApp con el mensaje pre-llenado.
//
// NOTA DE SEGURIDAD: el envío automático (Twilio u otro proveedor) debe
// implementarse en una función serverless (/api), NUNCA desde el navegador:
// las credenciales en código frontend quedan visibles para cualquier usuario.
// ─────────────────────────────────────────────────────────────────

// Limpia el número y agrega prefijo Perú
function formatNumero(tel) {
  const limpio = (tel || "").replace(/\D/g, "");
  return limpio.startsWith("51") ? `+${limpio}` : `+51${limpio}`;
}

// ─── ENVÍO PRINCIPAL ─────────────────────────────────────────────
async function enviarWA(telefono, mensaje) {
  if (!telefono) return { ok: false, error: "Sin teléfono" };
  const numero = formatNumero(telefono);
  abrirWALink(numero, mensaje);
  return { ok: true, metodo: "link" };
}

// Abre WhatsApp en el navegador
function abrirWALink(numero, mensaje) {
  const url = `https://wa.me/${numero.replace("+", "")}?text=${encodeURIComponent(mensaje)}`;
  window.open(url, "_blank");
}

// ═══════════════════════════════════════════════════════════════
// PLANTILLAS DE MENSAJES — edita los textos a tu gusto
// ═══════════════════════════════════════════════════════════════

// 1. Confirmación de pedido → CLIENTE
export async function notificarPedidoConfirmado(cliente, pedido) {
  const msg = `Hola ${cliente.nombre?.split(" ")[0]}, confirmamos tu pedido *#${pedido.numero}* 🏗

📦 *Materiales:* ${pedido.materiales}
📅 *Entrega estimada:* ${pedido.fechaEntrega}
🚚 *Transporte:* ${pedido.transporte || "Por asignar"}
💰 *Total:* S/ ${Number(pedido.valor).toLocaleString()}

Ante cualquier consulta escríbenos aquí mismo. ¡Gracias por confiar en nosotros!`;
  return enviarWA(cliente.telefono, msg);
}

// 2. Recordatorio de pago (3 días antes) → CLIENTE
export async function recordatorioPago(cliente, factura) {
  const msg = `Estimado ${cliente.nombre?.split(" ")[0]}, te recordamos que tu factura *#${factura.numero}* por *S/ ${Number(factura.monto).toLocaleString()}* vence el *${factura.fechaVencimiento}* 📋

Para coordinar el pago puedes escribirnos aquí o transferir a:
🏦 BCP: ${process.env.REACT_APP_CUENTA_BCP || "[Tu número de cuenta]"}

¡Gracias por tu puntualidad!`;
  return enviarWA(cliente.telefono, msg);
}

// 3. Factura vencida → CLIENTE
export async function alertaFacturaVencida(cliente, factura) {
  const msg = `${cliente.nombre?.split(" ")[0]}, tu factura *#${factura.numero}* por *S/ ${Number(factura.monto).toLocaleString()}* venció hoy ⚠️

Por favor regulariza el pago para continuar con futuros pedidos. Escríbenos para coordinar.`;
  return enviarWA(cliente.telefono, msg);
}

// 4. Cotización sin respuesta → CLIENTE
export async function seguimientoCotizacion(cliente, cotizacion) {
  const msg = `Hola ${cliente.nombre?.split(" ")[0]}, hace ${cotizacion.diasSinRespuesta} días te enviamos nuestra cotización de materiales 😊

¿Pudiste revisarla? Estamos disponibles para ajustar cantidades, precios o condiciones de pago.

¡Quedo atento para ayudarte con tu obra! 🏗`;
  return enviarWA(cliente.telefono, msg);
}

// 5. Pedido listo → TRANSPORTISTA
export async function avisarTransportista(transportista, pedido, cliente) {
  const msg = `${transportista.nombre?.split(" ")[0]}, tienes una carga lista para recojo 🚚

📋 *Pedido:* #${pedido.numero}
📦 *Carga:* ${pedido.materiales}
⚖️ *Peso aprox.:* ${pedido.pesoToneladas || "—"} TN
🏭 *Recojo:* ${process.env.REACT_APP_DIRECCION_ALMACEN || "[Dirección almacén]"}
📍 *Destino:* ${cliente.empresa} — ${pedido.direccionEntrega || cliente.ciudad}
📅 *Entrega:* ${pedido.fechaEntrega}

Confirma recibido respondiendo este mensaje ✅`;
  return enviarWA(transportista.telefono, msg);
}

// 6. Confirmación de entrega → CLIENTE
export async function confirmarEntrega(cliente, pedido) {
  const msg = `¡${cliente.nombre?.split(" ")[0]}, tu pedido *#${pedido.numero}* fue entregado! ✅

🕐 Hora de entrega: ${new Date().toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" })}
📦 Materiales: ${pedido.materiales}

¿Todo llegó en perfectas condiciones? Tu opinión nos ayuda a mejorar 🙏
Responde con una calificación del 1 al 5 ⭐`;
  return enviarWA(cliente.telefono, msg);
}

// 7. Alerta interna de mantenimiento → TU NÚMERO
export async function alertaMantenimiento(vehiculo, diasRestantes) {
  const tuNumero = process.env.REACT_APP_TELEFONO_ADMIN || "";
  const msg = `⚠️ *Alerta de mantenimiento*
Vehículo: *${vehiculo.placa}* (${vehiculo.tipo})
Conductor: ${vehiculo.conductor}
Mantenimiento en: *${diasRestantes} días* (${vehiculo.proximoMantenimiento})

Coordina con el taller y reasigna rutas si es necesario.`;
  return enviarWA(tuNumero, msg);
}

// 9. Abrir WhatsApp con texto libre (ej: guión del agente)
export function abrirWAConGuion(telefono, texto) {
  if (!telefono || !texto) return;
  abrirWALink(formatNumero(telefono), texto);
}

// 8. Cotización enviada → CLIENTE (botón manual)
export async function enviarCotizacion(cliente, items, condiciones) {
  const detalles = items.map(i => `• ${i.cantidad} ${i.unidad} de ${i.material}: S/ ${i.precio}`).join("\n");
  const msg = `Hola ${cliente.nombre?.split(" ")[0]}, aquí tienes tu cotización 📋

*${cliente.empresa}*
${detalles}

💰 *Total: S/ ${items.reduce((s, i) => s + i.precio, 0).toLocaleString()}*
📅 Válida por: 7 días
💳 Condiciones: ${condiciones || "Contado / 30 días crédito"}

Para confirmar el pedido responde este mensaje. ¡Gracias! 🏗`;
  return enviarWA(cliente.telefono, msg);
}
